/**
 * PATH: packages/email-hub/src/feature/components/FilterSidebar.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-FILTERSIDEBAR-001
 * PURPOSE: Canonical frame assembling typed email-filter sidebar sections.
 * WHY: Tenants consume this shared package body through thin re-export shims.
 * RELATED: packages/email-hub/src/feature/components/filter-sidebar/
 */
import { useEffect, useMemo, useState } from 'react'

import {
  useEmailFacets,
  useEmailFilterPresets,
  useEmailTags,
} from '../hooks/useEmails'
import {
  FlagsSection,
  StatusSection,
} from './filter-sidebar/BooleanSections'
import { CategorySection } from './filter-sidebar/CategorySection'
import { DateSection } from './filter-sidebar/DateSection'
import {
  countActive,
  loadExpand,
  saveExpand,
} from './filter-sidebar/expansion'
import { JobSection } from './filter-sidebar/JobSection'
import { MailboxSection } from './filter-sidebar/MailboxSection'
import { SavedViewsSection } from './filter-sidebar/SavedViewsSection'
import { SenderSection } from './filter-sidebar/SenderSection'
import { SidebarHeader } from './filter-sidebar/SidebarChrome'
import { TagsSection } from './filter-sidebar/TagsSection'
import type {
  ExpandState,
  FilterSidebarProps,
} from './filter-sidebar/types'

export type {
  CategorySidebarConfig,
  SidebarFilters,
} from './filter-sidebar/types'

export function FilterSidebar({
  categories,
  filters,
  onPatch,
  onClear,
  onApplyPreset,
}: FilterSidebarProps) {
  const { tags } = useEmailTags()
  const { facets, reload: reloadFacets } = useEmailFacets()
  const presetsApi = useEmailFilterPresets()
  const [expanded, setExpanded] = useState<ExpandState>(loadExpand)

  useEffect(() => { saveExpand(expanded) }, [expanded])

  const toggle = (section: keyof ExpandState) =>
    setExpanded((previous) => ({
      ...previous,
      [section]: !previous[section],
    }))

  // Surface a count next to "Filters" so the user can see at a
  // glance how many dimensions are currently constraining the list.
  const activeCount = useMemo(() => countActive(filters), [filters])

  return (
    <div
      className="w-60 border-r border-border h-full overflow-y-auto bg-muted/20 shrink-0"
      style={{ backgroundColor: 'var(--email-pane-fill)' }}
    >
      <div className="p-3">
        <SidebarHeader
          activeCount={activeCount}
          onClear={onClear}
          onSave={async (name) => {
            await presetsApi.save(name, filters as Record<string, unknown>)
          }}
          hasActive={activeCount > 0}
        />
        <SavedViewsSection
          expanded={expanded.saved}
          onToggle={() => toggle('saved')}
          presetsApi={presetsApi}
          onApplyPreset={onApplyPreset}
        />
        <CategorySection
          expanded={expanded.category}
          onToggle={() => toggle('category')}
          categories={categories}
          selected={filters.category}
          onSelect={(key) => onPatch({ category: key })}
          counts={facets?.categories}
        />
        <MailboxSection
          expanded={expanded.mailbox}
          onToggle={() => toggle('mailbox')}
          facets={facets}
          selectedKeys={filters.mailboxKeys || []}
          onChange={(keys) => onPatch({ mailboxKeys: keys })}
          onReload={reloadFacets}
        />
        <JobSection
          expanded={expanded.job}
          onToggle={() => toggle('job')}
          facets={facets}
          selectedIds={filters.jobIds || []}
          onChange={(ids) => onPatch({ jobIds: ids })}
        />
        <SenderSection
          expanded={expanded.sender}
          onToggle={() => toggle('sender')}
          facets={facets}
          value={filters.fromAddressContains || ''}
          onChange={(value) =>
            onPatch({ fromAddressContains: value || undefined })
          }
        />
        <DateSection
          expanded={expanded.date}
          onToggle={() => toggle('date')}
          gte={filters.receivedAtGte}
          lte={filters.receivedAtLte}
          onChange={(gte, lte) =>
            onPatch({ receivedAtGte: gte, receivedAtLte: lte })
          }
        />
        <FlagsSection
          expanded={expanded.flags}
          onToggle={() => toggle('flags')}
          hasAttachments={filters.hasAttachments}
          hasCalendarEvent={filters.hasCalendarEvent}
          onPatch={onPatch}
        />
        <StatusSection
          expanded={expanded.status}
          onToggle={() => toggle('status')}
          isRead={filters.isRead}
          isStarred={filters.isStarred}
          onPatch={onPatch}
        />
        <TagsSection
          expanded={expanded.tags}
          onToggle={() => toggle('tags')}
          tags={tags as Array<{ id: number; name: string; color: string }>}
        />
      </div>
    </div>
  )
}
